import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, money, API, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUSES = ["pending", "payment_received", "processing", "shipped", "completed", "cancelled"];
const label = (s) => s.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proofUrl, setProofUrl] = useState(null);

  const load = async () => setOrders((await api.get("/admin/orders")).data);
  useEffect(() => { load(); }, []);

  useEffect(() => {
    setProofUrl(null);
    if (sel?.payment_proof_file_id || sel?.payment_proof_path) {
      let revoked = false;
      api.get(`/admin/orders/${sel.id}/payment-proof`, { responseType: "blob" })
        .then((res) => { if (!revoked) setProofUrl(window.URL.createObjectURL(res.data)); })
        .catch(() => {});
      return () => { revoked = true; };
    }
  }, [sel]);

  const openOrder = (o) => { setSel(o); setNewStatus(o.status); setNote(""); setNotify(true); };

  const updateStatus = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${sel.id}/status`, { status: newStatus, notify, note });
      toast.success(data.notified ? `Status updated & customer notified ${sel.source === "web" ? "by email" : "on Telegram"}` : "Status updated");
      setSel(data.order);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/orders/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "orders.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const shown = orders.filter((o) => (filter === "all" ? true : o.status === filter));

  return (
    <div data-testid="orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Orders</h1>
          <p className="mt-1 text-sm text-muted-foreground">{orders.length} total orders</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44" data-testid="order-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={exportCsv} variant="outline" data-testid="export-orders-btn" className="rounded-lg"><Download className="mr-1.5 h-4 w-4" /> Export CSV</Button>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-white">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="overline">Order</TableHead>
            <TableHead className="overline">Customer</TableHead>
            <TableHead className="overline">Method</TableHead>
            <TableHead className="overline">Total</TableHead>
            <TableHead className="overline">Status</TableHead>
            <TableHead className="overline">Date</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No orders.</TableCell></TableRow>}
            {shown.map((o) => (
              <TableRow key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openOrder(o)} data-testid={`order-row-${o.id}`}>
                <TableCell className="font-semibold">{o.order_number}</TableCell>
                <TableCell>{o.customer?.name || "—"}</TableCell>
                <TableCell className="capitalize text-muted-foreground">{o.delivery_method === "delivery" ? "Shipping" : "Pickup"}</TableCell>
                <TableCell className="font-semibold">{money(o.total)}</TableCell>
                <TableCell><StatusBadge status={o.status} /></TableCell>
                <TableCell className="text-muted-foreground text-sm">{new Date(o.created_at).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-3">{sel.order_number} <StatusBadge status={sel.status} /><span className="rounded-full border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground" data-testid="order-source-badge">{sel.source === "web" ? "Website" : "Telegram"}</span></DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="overline text-muted-foreground mb-2">Customer</div>
                  <div className="font-medium">{sel.customer.name}</div>
                  <div className="text-muted-foreground">{sel.customer.email}</div>
                  <div className="text-muted-foreground">{sel.customer.phone}</div>
                  <div className="text-muted-foreground mt-1">{sel.customer.address}</div>
                  {sel.telegram_username && <div className="text-muted-foreground mt-1">@{sel.telegram_username}</div>}
                </div>
                <div>
                  <div className="overline text-muted-foreground mb-2">Summary</div>
                  <div className="flex justify-between"><span>Subtotal</span><span>{money(sel.subtotal)}</span></div>
                  {sel.discount_amount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount {sel.discount_code && `(${sel.discount_code})`}{sel.shipping_discount > 0 && ` incl. ${money(sel.shipping_discount)} off shipping`}</span><span>-{money(sel.discount_amount)}</span></div>}
                  {sel.auto_discount_amount > 0 && <div className="flex justify-between text-emerald-600"><span>Bank transfer discount ({sel.auto_discount_percent}%)</span><span>-{money(sel.auto_discount_amount)}</span></div>}
                  {sel.shipping_cost > 0 && <div className="flex justify-between"><span>Shipping</span><span>{money(sel.shipping_cost)}</span></div>}
                  <div className="flex justify-between font-bold mt-1 border-t border-border pt-1"><span>Total</span><span>{money(sel.total)}</span></div>
                  <div className="mt-1 text-muted-foreground capitalize">{sel.delivery_method === "delivery" ? "Shipping" : "Local Pickup"}</div>
                </div>
              </div>

              <div className="mt-2">
                <div className="overline text-muted-foreground mb-2">Items</div>
                <div className="rounded-lg border border-border divide-y divide-border">
                  {sel.items.map((i, idx) => (
                    <div key={idx} className="flex justify-between px-3 py-2 text-sm">
                      <span>{i.name}{i.size ? ` ${i.size}` : ""} × {i.qty}</span>
                      <span className="font-medium">{money(i.price * i.qty)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="overline text-muted-foreground mb-2">Payment Screenshot</div>
                {(sel.payment_proof_file_id || sel.payment_proof_path) ? (
                  proofUrl ? (
                    <a href={proofUrl} target="_blank" rel="noreferrer" data-testid="payment-proof-link">
                      <img src={proofUrl} alt="Payment proof" className="max-h-64 rounded-lg border border-border" data-testid="payment-proof-img" />
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground">Loading screenshot…</p>
                  )
                ) : (
                  <p className="text-sm text-muted-foreground" data-testid="no-payment-proof">No payment screenshot uploaded yet.</p>
                )}
              </div>

              <div className="rounded-lg bg-secondary/60 p-4">
                <div className="overline text-muted-foreground mb-3">Update Status</div>
                <div className="flex items-center gap-3">
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="w-52 bg-white" data-testid="order-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Checkbox id="notify" checked={notify} onCheckedChange={setNotify} data-testid="notify-checkbox" />
                    <Label htmlFor="notify" className="text-sm">Notify customer {sel.source === "web" ? "by email" : "on Telegram"}</Label>
                  </div>
                </div>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={`Optional note included in the ${sel.source === "web" ? "email" : "Telegram"} update…`} className="mt-3 bg-white" rows={2} data-testid="status-note" />
                <Button onClick={updateStatus} disabled={saving} data-testid="update-status-btn" className="mt-3 bg-primary text-primary-foreground hover:bg-[#7CBCE0]">{saving ? "Updating…" : "Update & Send"}</Button>
              </div>

              <div>
                <div className="overline text-muted-foreground mb-2">History</div>
                <div className="space-y-1 text-sm">
                  {(sel.status_history || []).map((h, i) => (
                    <div key={i} className="flex justify-between text-muted-foreground">
                      <span>{label(h.status)} {h.note && `— ${h.note}`}</span>
                      <span>{new Date(h.at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
